
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { Users, ChartNoAxesCombined, FileText } from 'lucide-react';
import { CircularProgress, Box } from '@mui/material';
import Employees from '../../components/Employees';
import PageHeader from '../../components/headers/Header';
import EmployeeOverview from '../../components/dash/Employees';
import { formatPositionsDisplay } from '../../services/positionAPIs';
import { getAllEmployees, getEmployeeImage } from '../../services/HRstaff';
import { getOffices } from '../../services/officeAPIs';
import DocumentDetailsModal from '../../components/DViews/DocDetailsModal';
import './Employees.css';
import { useMediaQuery } from '@mui/material';

// Import Documents component
import Documents from '../../components/Documents';

// Import document APIs
import { getAllDocuments } from '../../services/documentAPIs';

const ANIMATION_DELAY_MS = 300;

const EmployeeManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Read tab and office from query string
  const params = new URLSearchParams(location.search);
  const defaultTab = params.get("tab") || "employee";
  const pathOffice = params.get("office") || 'All';

  // State for data
  const [employees, setEmployees] = useState([]); // Transformed for overview/employees tabs
  // Raw employees state for Documents compatibility
  const [rawEmployees, setRawEmployees] = useState([]);
  const [profileImages, setProfileImages] = useState({});
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOffice, setSelectedOffice] = useState(pathOffice);
  const [animateStats, setAnimateStats] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  // States for Documents
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // For the Employees tab
  const [hasFetchedEmployeeTabData, setHasFetchedEmployeeTabData] = useState(false);

  const isLargeScreen = useMediaQuery('(min-width: 1600px)');
  const [activeTab, setActiveTab] = useState(defaultTab);
  const tableWrapperRef = useRef(null);

  // Simplified handler to switch to Employees tab
  const handleNavigateToEmployees = useCallback(() => {
    setActiveTab('employee');
  }, []);

  const calculateDaysWorked = useCallback((joiningDate) => {
    if (!joiningDate) return 0;
    const joinDate = new Date(joiningDate);
    const today = new Date();
    const diffTime = Math.abs(today - joinDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }, []);

  // Fetch both raw employees and documents (for Documents tab refreshes)
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [employeeData, documentData] = await Promise.all([
        getAllEmployees(),
        getAllDocuments(),
      ]);
      console.log('Fetched Documents:', documentData);
      console.log('Raw Employees:', employeeData);
      // Set raw for Documents
      setRawEmployees(employeeData);
      // Do NOT set transformed employees here to avoid breaking other tabs
      setDocuments(documentData);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 👉 FETCH OFFICES ON MOUNT
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const officeData = await getOffices();
        setOffices(officeData || []);
        const validOffice = officeData.find(office => office.name.toLowerCase() === pathOffice.toLowerCase());
        setSelectedOffice(validOffice ? validOffice.name : 'All');
      } catch (err) {
        console.error('Failed to fetch offices:', err);
        setError('Failed to load office data.');
      }
    };

    fetchOffices();
  }, [pathOffice]);

  // 👉 SYNC activeTab WITH URL QUERY
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  // 👉 UPDATE URL WHEN selectedOffice OR tab CHANGES
  useEffect(() => {
    if (activeTab === 'employee') {
      const officeQuery = selectedOffice && selectedOffice !== 'All' ? `&office=${encodeURIComponent(selectedOffice)}` : '';
      navigate(`/employees?tab=${activeTab}${officeQuery}`);
    } else if (activeTab === 'overview') {
      navigate(`/employees?tab=${activeTab}`);
    } 
  }, [selectedOffice, activeTab, navigate]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    const officeQuery = selectedOffice && selectedOffice !== 'All' ? `&office=${encodeURIComponent(selectedOffice)}` : '';
    navigate(`/employees?tab=${tab}${officeQuery}`);
  }, [selectedOffice, navigate]);

  // ✅✅✅ CRITICAL FIX: FETCH EMPLOYEES ON COMPONENT MOUNT — AVAILABLE FOR ALL TABS
  useEffect(() => {
    let animationTimer;
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError(null);

        const employeeData = await getAllEmployees();

        // Set raw for Documents
        setRawEmployees(employeeData);

        const transformedEmployees = employeeData.map(employee => ({
          id: employee.employeeId || '',
          name: employee.firstName || 'N/A',
          surname: employee.lastName || 'N/A',
          department: employee.department?.name || 'No department',
          position: formatPositionsDisplay(employee),
          office: employee.office?.name || 'No office', // ✅ Fixed: Extract name if object
          status: 'Active',
          avatar: employee.gender === 'MALE' ? '👨' : '👩',
          daysWorked: calculateDaysWorked(employee.dateOfJoining),
          leaves: 0,
          absent: 0,
          contractType: employee.contractType || 'N/A',
          hasImage: !!employee.image,
          personalInfo: { location: employee.office?.name || 'Unknown' }, // ✅ Fixed: Extract name
          email: employee.user?.email || 'No email',
          joiningDate: employee.dateOfJoining || '',
          dob: employee.dob || '',
          phone: employee.phone || '',
          gender: employee.gender || '',
          cin: employee.cin || '',
          cnss: employee.cnss || '',
          diploma: employee.diploma || '',
        }));

        setEmployees(transformedEmployees);
        setAnimateStats(false);
        animationTimer = setTimeout(() => setAnimateStats(true), ANIMATION_DELAY_MS);
      } catch (err) {
        setError('Failed to load employee data. Please try again later.');
        console.error('Fetch initial data error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();

    return () => {
      if (animationTimer) clearTimeout(animationTimer);
    };
  }, [calculateDaysWorked]); // ✅ Depend on calculateDaysWorked

  // 👇 FETCH DETAILED DATA FOR EMPLOYEES TAB ONLY WHEN IT'S CLICKED FOR THE FIRST TIME
  useEffect(() => {
    if (activeTab !== 'employee' || hasFetchedEmployeeTabData) return;

    const fetchEmployeeTabData = async () => {
      try {
        setLoading(true);
        setError(null);

        const employeesWithImages = employees.filter(emp => emp.hasImage);

        if (employeesWithImages.length > 0) {
          const batchSize = 5;
          for (let i = 0; i < employeesWithImages.length; i += batchSize) {
            const batch = employeesWithImages.slice(i, i + batchSize);
            const imagePromises = batch.map(async (emp) => {
              try {
                const imageUrl = await getEmployeeImage(emp.id);
                return { id: emp.id, imageUrl };
              } catch (err) {
                return { id: emp.id, imageUrl: null };
              }
            });

            const images = await Promise.all(imagePromises);
            const imageMap = images.reduce((acc, { id, imageUrl }) => {
              if (imageUrl) acc[id] = imageUrl;
              return acc;
            }, {});

            setProfileImages(prev => ({ ...prev, ...imageMap }));
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        setHasFetchedEmployeeTabData(true);

      } catch (err) {
        setError('Failed to load detailed employee data.');
        console.error('Fetch employee tab data error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeTabData();

    return () => {
      Object.values(profileImages).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [activeTab, employees, hasFetchedEmployeeTabData, profileImages]);

  // ✅ Fetch documents (and raw employees) when "documents" tab is first opened
  useEffect(() => {
    if (activeTab === 'documents' && documents.length === 0) {
      fetchData();
    }
  }, [activeTab, documents.length, fetchData]);

  // Filtered employees (memoized)
  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      const fullName = `${emp.name} ${emp.surname}`.toLowerCase();
      const matchesSearch = fullName.includes(searchTerm.toLowerCase());
      // ✅ Fixed: office is now consistently a string
      const matchesOffice = selectedOffice === 'All' ||
        emp.office.toLowerCase() === selectedOffice.toLowerCase();
      return matchesSearch && matchesOffice;
    });
  }, [employees, searchTerm, selectedOffice]);

  // Stats for overview (memoized)
  const statusStats = useMemo(() => ({
    active: employees.filter(e => e.status === 'Active').length,
    inactive: employees.filter(e => e.status === 'Inactive').length,
  }), [employees]);

  // Pie chart data for overview (memoized)
  const pieChartData = useMemo(() => {
    const total = statusStats.active + statusStats.inactive;
    const activePercentage = total > 0 ? (statusStats.active / total) * 100 : 0;
    const inactivePercentage = total > 0 ? (statusStats.inactive / total) * 100 : 0;
    const activeAngle = (activePercentage / 100) * 360;
    const midAngleActive = (270 + activeAngle / 2) % 360;
    const midAngleInactive = (270 + activeAngle + (360 - activeAngle) / 2) % 360;
    const labelRadius = 42;
    const activeX = 90 + labelRadius * Math.cos((midAngleActive * Math.PI) / 180);
    const activeY = 90 + labelRadius * Math.sin((midAngleActive * Math.PI) / 180);
    const inactiveX = 90 + labelRadius * Math.cos((midAngleInactive * Math.PI) / 180);
    const inactiveY = 90 + labelRadius * Math.sin((midAngleInactive * Math.PI) / 180);
    return { total, activePercentage, inactivePercentage, activeX, activeY, inactiveX, inactiveY };
  }, [statusStats]);

  // Handlers
  const handleSearchChange = useCallback(e => setSearchTerm(e.target.value), []);
  const handleOfficeChange = useCallback(e => {
    const newOffice = e.target.value;
    setSelectedOffice(newOffice);
  }, []);

  const handleViewClick = useCallback((employee) => {
    setSelectedEmployeeId(employee.id);
  }, []);

  const handleCloseView = useCallback(() => {
    setSelectedEmployeeId(null);
  }, []);

  const handleViewDetails = useCallback((employeeId) => {
    navigate(`/view-profile/${employeeId}`);
  }, [navigate]);

  const getDepartmentColor = (index) => {
    const colors = ['#4CAF50', '#2196F3', '#FF9800', '#F44336', '#9C27B0'];
    return colors[index % colors.length];
  };

  // Handle document selection
  const handleDocSelect = useCallback((doc) => {
    setSelectedDoc(doc);
    console.log('Selected document:', doc);
  }, []);

  return (
    <div className="Leaves-main-container">
      <div className="Leaves-content-wrapper">
        <div className="Leaves-nav-tabs">
          <div className="Leaves-tab-bar">
            {[
              { id: 'overview', name: 'Overview', icon: ChartNoAxesCombined },
              { id: 'employee', name: 'Employees', icon: Users },
              
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`Leaves-tab-btn ${activeTab === tab.id ? 'Leaves-tab-btn-active' : ''}`}
              >
                <tab.icon className="Leaves-tab-icon" />
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {/* Render Overview Tab */}
        {activeTab === 'overview' && (
          loading && employees.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            <EmployeeOverview
              employees={employees}
              profileImages={profileImages}
              statusStats={statusStats}
              animateStats={animateStats}
              activePercentage={pieChartData.activePercentage}
              inactivePercentage={pieChartData.inactivePercentage}
              total={pieChartData.total}
              activeX={pieChartData.activeX}
              activeY={pieChartData.activeY}
              inactiveX={pieChartData.inactiveX}
              inactiveY={pieChartData.inactiveY}
              onNavigateToEmployees={handleNavigateToEmployees}
            />
          )
        )}

        {/* Render Employees Tab */}
        {activeTab === 'employee' && (
          loading && !hasFetchedEmployeeTabData ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            <Employees
              employees={employees}
              profileImages={profileImages}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedOffice={selectedOffice}
              setSelectedOffice={setSelectedOffice}
              handleOfficeChange={handleOfficeChange}
              handleViewClick={handleViewClick}
              handleCloseView={handleCloseView}
              selectedEmployeeId={selectedEmployeeId}
              handleViewDetails={handleViewDetails}
              filteredEmployees={filteredEmployees}
              handleSearchChange={handleSearchChange}
            />
          )
        )}

        

        {/* ✅ Document Details Modal — single instance at the end */}
        {selectedDoc && (
          <DocumentDetailsModal
            selectedDocument={selectedDoc}
            setSelectedDocument={setSelectedDoc}
            fetchDocuments={fetchData}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
          />
        )}
      </div>
    </div>
  );
};

export default EmployeeManagement;
